"use client";

import Image from "next/image";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { ArrowRight, ChevronLeft, ChevronRight, X } from "lucide-react";
import Link from "@/components/LocaleLink";
import { GoogleGIcon, TrustpilotStarIcon } from "@/components/ReviewPlatformIcons";
import { cn } from "@/lib/utils";
import type { Layout } from "./cases";
import type { Story, StoryQuote } from "./stories";
import { Fancy } from "./fancy";

export type PortfolioPicture = {
  src: string;
  alt: string;
  position?: string;
  stock?: boolean;
  credit?: string;
  creditLinks?: { source: string; license: string };
  landscape?: boolean;
};

export type PortfolioItem = Omit<Story, "pictures"> & {
  layout: Layout;
  /** Month and year, already in the page language. */
  when?: string;
  anonymous?: boolean;
  /** The first one is the cover. */
  pictures: PortfolioPicture[];
};

export type PortfolioLabels = {
  anonymousNote: string;
  stockNote: string;
  photo: string;
  close: string;
  prev: string;
  next: string;
  nextCase: string;
  endBody: string;
  readTranslation: string;
  quotes: [string, string];
  tileTitle: string;
  tileBody: string;
  tileCta: string;
  ctaLabel: string;
};

/** Each cover's cell in the grid and the shape of its picture. */
const TILE: Record<Layout, { cell: string; frame: string; sizes: string }> = {
  lead: {
    cell: "sm:col-span-2 lg:col-span-7",
    frame: "aspect-[4/3] sm:aspect-[16/9] lg:aspect-auto lg:h-[34rem]",
    sizes: "(min-width: 1024px) 680px, 100vw",
  },
  side: {
    cell: "lg:col-span-5",
    frame: "aspect-[4/5] lg:aspect-auto lg:h-[34rem]",
    sizes: "(min-width: 1024px) 480px, (min-width: 640px) 50vw, 100vw",
  },
  third: {
    cell: "lg:col-span-4",
    frame: "aspect-[4/3] sm:aspect-[4/5]",
    sizes: "(min-width: 1024px) 380px, (min-width: 640px) 50vw, 100vw",
  },
  closing: {
    cell: "sm:col-span-2 lg:col-span-7",
    frame: "aspect-[4/3] sm:aspect-[16/9] lg:aspect-auto lg:h-[28rem]",
    sizes: "(min-width: 1024px) 680px, 100vw",
  },
};

function PlatformIcon({ platform, className }: { platform: "google" | "trustpilot"; className?: string }) {
  return platform === "google" ? <GoogleGIcon className={className} /> : <TrustpilotStarIcon className={className} />;
}

/**
 * A few of our cases, shown the way a studio shows its projects (operator,
 * 01.11.2026): big pictures in an uneven grid, a small line with place and
 * kind of help, the title. A click opens the case over the site (redesign
 * 01.11.2026, "wenn man draufklickt nicht so schön, auch mobile"):
 *
 * - a bar that stays on top with "2 / 6", back, next and close, so a phone
 *   can step through the cases without scrolling to the end;
 * - the opening photo full width with the title on it;
 * - the facts in one row and the date that changed right below, so the
 *   result is seen before the story;
 * - the story, more photos, the couple's words, one invitation, the next case.
 *
 * Every case is in the HTML (closed dialog), so search engines read them all;
 * `#id` in the address opens one directly.
 */
export function Portfolio({ items, labels }: { items: PortfolioItem[]; labels: PortfolioLabels }) {
  const [openId, setOpenId] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const at = items.findIndex((it) => it.id === openId);

  const open = (id: string) => {
    setOpenId(id);
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) dialog.showModal();
    history.replaceState(null, "", `#${id}`);
    // Focus the case, not the close button, so it opens without a focus ring and the arrow keys work at once.
    scrollRef.current?.focus({ preventScroll: true });
    scrollRef.current?.scrollTo({ top: 0 });
  };

  // A shared link to "#holbaek" opens that case, on arrival and on back/forward.
  useEffect(() => {
    const fromHash = () => {
      const id = decodeURIComponent(location.hash.slice(1));
      if (items.some((it) => it.id === id)) open(id);
    };
    const frame = requestAnimationFrame(fromHash);
    window.addEventListener("hashchange", fromHash);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("hashchange", fromHash);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the cases never change on the page
  }, []);

  // The page behind does not scroll while a case is open.
  useEffect(() => {
    if (!openId) return;
    const root = document.documentElement;
    const before = root.style.overflow;
    root.style.overflow = "hidden";
    return () => {
      root.style.overflow = before;
    };
  }, [openId]);

  const step = (by: number) => {
    open(items[(Math.max(at, 0) + by + items.length) % items.length].id);
  };

  const barButton =
    "focus-ring grid h-10 w-10 shrink-0 place-items-center rounded-full text-ink-900 ring-1 ring-navy-200 transition hover:bg-cream-100 hover:ring-navy-300";

  return (
    <>
      <ul className="grid gap-x-5 gap-y-12 sm:grid-cols-2 lg:grid-cols-12 lg:gap-x-6 lg:gap-y-16">
        {items.flatMap((it) => {
          const tile = <Tile key={it.id} item={it} onOpen={() => open(it.id)} />;
          // The closing row: the invitation, then our own story (on phones the invitation comes last).
          return it.layout === "closing" ? [<CtaTile key="cta" labels={labels} />, tile] : [tile];
        })}
      </ul>

      <dialog
        ref={dialogRef}
        onClose={() => {
          setOpenId(null);
          history.replaceState(null, "", location.pathname + location.search);
        }}
        onKeyDown={(event) => {
          if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
          const forward = (event.key === "ArrowRight") !== (document.dir === "rtl");
          step(forward ? 1 : -1);
        }}
        aria-label={items[at]?.title}
        // inset-0 instead of 100dvh/100vw: the desktop page is zoomed to 90 % (globals.css), viewport units would leave a strip.
        className="fixed inset-0 m-0 h-auto max-h-none w-auto max-w-none bg-transparent p-0 backdrop:bg-navy-950/85"
      >
        <div
          ref={scrollRef}
          data-scroll
          tabIndex={-1}
          onClick={(event) => {
            if (event.target === scrollRef.current) dialogRef.current?.close();
          }}
          className="h-full overflow-y-auto overscroll-contain outline-none sm:px-6 sm:py-8 xl:px-24"
        >
          <div className="mx-auto min-h-full max-w-[68rem] bg-white sm:min-h-0 sm:overflow-clip sm:rounded-2xl">
            {/* Stays on top while the case scrolls: where we are, back, next, close. */}
            <div className="sticky top-0 z-20 flex h-14 items-center justify-between gap-3 border-b border-navy-100 bg-white/95 px-4 backdrop-blur sm:px-6">
              <p className="min-w-0 truncate text-[13px] text-navy-500">
                {/* dir="ltr": in Arabic the "2 / 6" otherwise ran into the place name. */}
                <span dir="ltr" className="inline-block font-semibold tabular-nums text-ink-900">
                  {at + 1} / {items.length}
                </span>
                {items[at] ? <span className="ms-2.5">{items[at].place}</span> : null}
              </p>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => step(-1)} aria-label={labels.prev} className={barButton}>
                  <ChevronLeft className="h-5 w-5 rtl:-scale-x-100" aria-hidden />
                </button>
                <button type="button" onClick={() => step(1)} aria-label={labels.next} className={barButton}>
                  <ChevronRight className="h-5 w-5 rtl:-scale-x-100" aria-hidden />
                </button>
                <button
                  type="button"
                  onClick={() => dialogRef.current?.close()}
                  aria-label={labels.close}
                  className="focus-ring ms-1 grid h-10 w-10 shrink-0 place-items-center rounded-full bg-ink-900 text-white transition hover:bg-navy-800"
                >
                  <X className="h-5 w-5" aria-hidden />
                </button>
              </div>
            </div>
            {items.map((it, i) => (
              <CaseView
                key={it.id}
                item={it}
                next={items[(i + 1) % items.length]}
                labels={labels}
                hidden={it.id !== openId}
                onNext={() => step(1)}
              />
            ))}
          </div>
        </div>
      </dialog>
    </>
  );
}

function DatesLine({ dates }: { dates: NonNullable<Story["dates"]> }) {
  return (
    <span className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[14px] leading-snug">
      {dates.before ? <span className={cn("text-navy-500", dates.strike && "line-through")}>{dates.before}</span> : null}
      <ArrowRight className="h-3.5 w-3.5 self-center text-danish-700 rtl:-scale-x-100" aria-hidden />
      <span className="font-semibold text-ink-900">{dates.after}</span>
    </span>
  );
}

function Tile({ item: it, onOpen }: { item: PortfolioItem; onOpen: () => void }) {
  const cover = it.pictures[0];
  const tile = TILE[it.layout];
  return (
    <li className={tile.cell}>
      <button type="button" onClick={onOpen} aria-haspopup="dialog" className="focus-ring group block w-full rounded-lg text-start">
        <span className={cn("relative block w-full overflow-hidden rounded-lg bg-cream-200", tile.frame)}>
          <Image
            src={cover.src}
            alt={cover.alt}
            fill
            sizes={tile.sizes}
            quality={80}
            className="object-cover transition duration-[900ms] ease-out group-hover:scale-[1.04]"
            style={cover.position ? { objectPosition: cover.position } : undefined}
          />
          <span className="absolute inset-0 bg-ink-900/0 transition duration-500 group-hover:bg-ink-900/10" aria-hidden />
          {/* Where the photo was taken elsewhere, the cover says so (Holbæk: a photo from Croatia). */}
          {it.coverNote ? (
            <span className="absolute bottom-3 start-3 rounded-full bg-black/45 px-2.5 py-1 text-[11px] font-medium text-white backdrop-blur-sm">
              {it.coverNote}
            </span>
          ) : null}
        </span>
        <span className="mt-4 block text-[11.5px] font-semibold uppercase tracking-[0.18em] text-danish-700 rtl:tracking-normal">
          {it.place} · {it.category}
        </span>
        <span
          className={cn(
            "mt-1.5 block text-balance font-display font-normal leading-[1.2] text-ink-900 transition group-hover:text-danish-700",
            it.layout === "third" ? "text-[1.4rem]" : "text-[1.5rem] sm:text-[1.75rem]",
          )}
        >
          {it.title}
        </span>
        {it.dates ? <DatesLine dates={it.dates} /> : null}
      </button>
    </li>
  );
}

function CtaTile({ labels }: { labels: PortfolioLabels }) {
  return (
    <li className="order-last sm:col-span-2 lg:order-none lg:col-span-5">
      <div className="flex h-full min-h-[20rem] flex-col justify-end rounded-lg bg-navy-950 p-6 min-[360px]:p-8 sm:p-10 lg:h-[28rem] lg:min-h-0">
        <span className="mb-5 text-[2.6rem] leading-none" aria-hidden>
          💍
        </span>
        <p className="text-balance font-display text-[2rem] font-normal leading-[1.12] text-white sm:text-[2.4rem]">
          <Fancy text={labels.tileTitle} accent="italic text-danish-300" />
        </p>
        <p className="mt-4 max-w-sm text-[15px] leading-[1.7] text-white/75">{labels.tileBody}</p>
        <Link
          href="/eignungs-check"
          data-track="cta_cases_tile"
          className="focus-ring-dark group mt-8 inline-flex min-h-12 items-center gap-2 self-start whitespace-nowrap rounded-full bg-coral-500 px-5 text-[14.5px] font-semibold text-white transition hover:-translate-y-px hover:bg-coral-600 min-[360px]:px-6 min-[360px]:text-[15px]"
        >
          {labels.tileCta}
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 rtl:-scale-x-100" aria-hidden />
        </Link>
      </div>
    </li>
  );
}

/** "Photographer, CC BY 2.0" with links to the source page and the licence, where the licence asks for them. */
export function Credit({ pic }: { pic: PortfolioPicture }) {
  if (!pic.credit) return null;
  const [who, ...rest] = pic.credit.split(", ");
  const licence = rest.join(", ");
  if (!pic.creditLinks) return <>{pic.credit}</>;
  return (
    <>
      <a href={pic.creditLinks.source} target="_blank" rel="noopener" className="underline decoration-navy-300 underline-offset-2 hover:text-ink-900">
        {who}
      </a>
      {licence ? ", " : null}
      {licence ? (
        <a href={pic.creditLinks.license} target="_blank" rel="noopener license" className="underline decoration-navy-300 underline-offset-2 hover:text-ink-900">
          {licence}
        </a>
      ) : null}
    </>
  );
}

/** One photo of the opened case, its caption below. */
function Photo({ pic, frame, sizes, labels, note }: { pic: PortfolioPicture; frame: string; sizes: string; labels: PortfolioLabels; note?: ReactNode }) {
  return (
    <figure>
      <div className={cn("relative w-full overflow-hidden bg-cream-200 sm:rounded-lg", frame)}>
        <Image
          src={pic.src}
          alt={pic.alt}
          fill
          sizes={sizes}
          quality={85}
          className="object-cover"
          style={pic.position ? { objectPosition: pic.position } : undefined}
        />
      </div>
      <figcaption className="px-6 pt-2.5 text-[12px] leading-relaxed text-navy-500 sm:px-0">
        {pic.alt}
        {pic.credit ? (
          <>
            {` · ${labels.photo}: `}
            <Credit pic={pic} />
          </>
        ) : null}
        {note}
      </figcaption>
    </figure>
  );
}

function Quote({ quote, labels }: { quote: StoryQuote; labels: PortfolioLabels }) {
  return (
    <figure className="mx-auto max-w-[42rem]">
      <span className="block h-px w-12 bg-danish-700" aria-hidden />
      <blockquote dir="auto" className="mt-6 text-balance font-display text-[1.4rem] italic leading-[1.5] text-ink-900 sm:text-[1.75rem]">
        {labels.quotes[0]}
        {quote.text}
        {labels.quotes[1]}
      </blockquote>
      <figcaption className="mt-5 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[13px] text-navy-500">
        {quote.platform ? <PlatformIcon platform={quote.platform} className="h-4 w-4" /> : null}
        <span className="font-semibold text-ink-900">{quote.source}</span>
        {quote.platform ? (
          <span className="tracking-[0.1em] text-[#FBBC04]" aria-label="5 / 5">
            ★★★★★
          </span>
        ) : null}
        {quote.note ? <span>{quote.note}</span> : null}
      </figcaption>
      {quote.translation ? (
        <details className="mt-4">
          <summary className="cursor-pointer text-[13px] font-medium text-navy-600 underline decoration-navy-300 underline-offset-4 hover:text-ink-900">
            {labels.readTranslation}
          </summary>
          <p className="mt-3 text-[15.5px] italic leading-[1.7] text-navy-700">{quote.translation}</p>
        </details>
      ) : null}
    </figure>
  );
}

function CaseView({
  item: it,
  next,
  labels,
  hidden,
  onNext,
}: {
  item: PortfolioItem;
  next: PortfolioItem;
  labels: PortfolioLabels;
  hidden: boolean;
  onNext: () => void;
}) {
  const [hero, ...more] = it.pictures;
  const pad = "px-6 sm:px-12 lg:px-16";
  const inset = "mx-6 sm:mx-12 lg:mx-16";
  return (
    <article hidden={hidden}>
      {/* The opening photo, full width, with the title on it. */}
      <figure>
        <div className="relative aspect-[4/5] w-full overflow-hidden bg-navy-900 sm:aspect-[16/9]">
          <Image
            src={hero.src}
            alt={hero.alt}
            fill
            sizes="(min-width: 1088px) 1088px, 100vw"
            quality={85}
            className="object-cover"
            style={hero.position ? { objectPosition: hero.position } : undefined}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent" aria-hidden />
          <div className={cn(pad, "absolute inset-x-0 bottom-0 pb-8 text-white sm:pb-12")}>
            <p className="text-[12px] font-semibold uppercase tracking-[0.2em] text-white/80 rtl:tracking-normal">
              {it.place} · {it.category}
            </p>
            <h2 className="mt-3 max-w-3xl text-balance font-display text-[2.2rem] font-normal leading-[1.06] [text-shadow:0_2px_18px_rgba(0,0,0,.35)] sm:text-[3.5rem]">
              {it.title}
            </h2>
            <p className="mt-3 text-[15px] text-white/85">
              {it.who}
              {it.when ? ` · ${it.when}` : ""}
            </p>
          </div>
        </div>
        <figcaption className={cn(pad, "pt-3 text-[12px] leading-relaxed text-navy-500")}>
          {hero.alt}
          {hero.credit ? (
            <>
              {` · ${labels.photo}: `}
              <Credit pic={hero} />
            </>
          ) : null}
          {/* Anonymous couples: the photo shows the place, or plainly says it is not their wedding. */}
          {it.anonymous ? ` · ${hero.stock ? labels.stockNote : labels.anonymousNote}` : null}
        </figcaption>
      </figure>

      {/* The facts in one row, then the date that changed: the result before the story. */}
      <dl className={cn(inset, "mt-8 grid grid-cols-2 gap-x-6 gap-y-5 border-y border-navy-100 py-6 sm:grid-cols-4")}>
        {it.facts.map(([term, value]) => (
          <div key={term} className="min-w-0">
            <dt className="text-[11px] font-semibold uppercase tracking-[0.18em] text-navy-500 rtl:tracking-normal">{term}</dt>
            <dd className="mt-1 text-[15px] leading-snug text-ink-900">{value}</dd>
          </div>
        ))}
      </dl>

      {it.dates ? (
        <div className={cn(inset, "mt-8 rounded-2xl bg-cream-100 px-6 py-7 text-center sm:px-10 sm:py-9")}>
          <p className="text-[12px] font-semibold uppercase tracking-[0.2em] text-navy-500 rtl:tracking-normal">{it.dates.label}</p>
          <p className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 font-display">
            {it.dates.before ? (
              <>
                <span className={cn("text-[1.25rem] text-navy-500 sm:text-[1.6rem]", it.dates.strike && "line-through decoration-danish-700/70 decoration-2")}>
                  {it.dates.before}
                </span>
                <ArrowRight className="h-5 w-5 text-danish-700 rtl:-scale-x-100" aria-hidden />
              </>
            ) : null}
            <span className="text-[2rem] leading-tight text-danish-700 sm:text-[2.8rem]">{it.dates.after}</span>
          </p>
          {it.dates.note ? <p className="mt-2 text-[15px] text-navy-600">{it.dates.note}</p> : null}
        </div>
      ) : null}

      <div className={cn(pad, "py-10 sm:py-14")}>
        <div className="mx-auto max-w-[42rem] space-y-5">
          {it.story.map((para, i) => (
            <p
              key={para}
              className={
                i === 0
                  ? "font-display text-[1.35rem] leading-[1.55] text-ink-900 sm:text-[1.55rem]"
                  : "text-[17px] leading-[1.8] text-ink-800"
              }
            >
              {para}
            </p>
          ))}
        </div>
      </div>

      {more.length ? (
        <div className={cn("grid gap-y-8 sm:gap-x-4 sm:px-12 lg:px-16", more.length > 1 && "sm:grid-cols-2")}>
          {more.map((pic) => (
            <Photo
              key={pic.src}
              pic={pic}
              frame={more.length > 1 ? (pic.landscape ? "aspect-[4/3]" : "aspect-[4/5]") : "aspect-[3/2]"}
              sizes={more.length > 1 ? "(min-width: 640px) 470px, 100vw" : "(min-width: 1088px) 960px, 100vw"}
              labels={labels}
            />
          ))}
        </div>
      ) : null}

      {it.quote ? (
        <div className={cn(pad, "pb-4 pt-14 sm:pt-20")}>
          <Quote quote={it.quote} labels={labels} />
        </div>
      ) : null}

      {/* One calm invitation instead of a "thanks for viewing". */}
      <div className={cn(pad, "py-12 sm:py-16")}>
        <div className="rounded-2xl bg-navy-950 px-6 py-9 text-center sm:px-10 sm:py-11">
          <p className="mx-auto max-w-lg text-balance font-display text-[1.45rem] leading-snug text-white sm:text-[1.75rem]">{labels.endBody}</p>
          <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              href="/eignungs-check"
              data-track="cta_cases_dialog"
              className="focus-ring-dark group inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-coral-500 px-6 text-[15px] font-semibold text-white transition hover:-translate-y-px hover:bg-coral-600"
            >
              {labels.ctaLabel}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 rtl:-scale-x-100" aria-hidden />
            </Link>
            {it.link ? (
              <Link
                href={it.link.href}
                className="focus-ring-dark inline-flex min-h-12 items-center justify-center rounded-full px-6 text-[15px] font-semibold text-white ring-1 ring-white/35 transition hover:-translate-y-px hover:bg-white/10"
              >
                {it.link.label}
              </Link>
            ) : null}
          </div>
        </div>
      </div>

      {/* The next case, as a picture to step into. */}
      <button type="button" onClick={onNext} className="focus-ring-dark group relative block h-44 w-full overflow-hidden text-start sm:h-56">
        <Image
          src={next.pictures[0].src}
          alt=""
          fill
          sizes="(min-width: 1088px) 1088px, 100vw"
          quality={70}
          className="object-cover brightness-[0.55] transition duration-[900ms] ease-out group-hover:scale-[1.03]"
          style={next.pictures[0].position ? { objectPosition: next.pictures[0].position } : undefined}
        />
        <span className={cn(pad, "absolute inset-0 flex items-center justify-between gap-6 text-white")}>
          <span>
            <span className="block text-[12px] font-semibold uppercase tracking-[0.2em] text-white/80 rtl:tracking-normal">{labels.nextCase}</span>
            <span className="mt-2 block text-balance font-display text-[1.6rem] leading-[1.15] sm:text-[2.2rem]">{next.title}</span>
          </span>
          <ArrowRight className="h-7 w-7 shrink-0 transition-transform group-hover:translate-x-1 rtl:-scale-x-100" aria-hidden />
        </span>
      </button>
    </article>
  );
}
